import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom';
import Home from './pages/Home';
import OneRMTracker from './pages/OneRMTracker';
import OrderDashboard from './pages/OrderDashboard';
import ReceiptScanner from './pages/ReceiptScanner';
import PokemonCenter from './pages/PokemonCenter';
import LoginScreen from './components/LoginScreen';
import './App.css';

const NAV_SECTIONS = [
  {
    label: 'Gym',
    links: [
      { to: '/', end: true, icon: '🏠', text: 'Dashboard' },
      { to: '/1rm',          icon: '💪', text: '1RM Tracker' },
    ],
  },
  {
    label: 'Elki Solutions',
    links: [
      { icon: '💰', text: 'Margin Calculator', disabled: true },
      { to: '/orders',   icon: '📦', text: 'Order Dashboard' },
      { to: '/receipts', icon: '🧾', text: 'Receipt Scanner' },
      { to: '/pokemon',  icon: '⚡', text: 'Pokémon Center' },
    ],
  },
  {
    label: 'NHS & Career',
    links: [
      { icon: '🏥', text: 'Specialty Matrix',  disabled: true },
      { icon: '📈', text: 'Portfolio Tracker', disabled: true },
    ],
  },
];

const COLLAPSE_KEY = 'hq.sidebarCollapsed';

export default function App() {
  const [authState, setAuthState] = useState('loading'); // loading | ok | denied
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });

  const toggleSidebar = () => setCollapsed(c => {
    const next = !c;
    try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch {}
    return next;
  });

  useEffect(() => {
    fetch('/api/me', { credentials: 'include' })
      .then(r => setAuthState(r.ok ? 'ok' : 'denied'))
      .catch(() => {
        // Backend unreachable (e.g. CRA dev without backend) — show app, it will
        // display its own "backend not running" message per page.
        setAuthState('ok');
      });
  }, []);

  if (authState === 'loading') {
    return (
      <div style={{
        minHeight: '100vh', background: '#0f172a',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ color: '#334155', fontSize: 14 }}>Loading…</div>
      </div>
    );
  }

  if (authState === 'denied') {
    return <LoginScreen onAuthenticated={() => setAuthState('ok')} />;
  }

  return (
    <BrowserRouter>
      <div className={collapsed ? 'layout collapsed' : 'layout'}>
        <nav className="sidebar">
          <button
            className="sidebar-toggle"
            onClick={toggleSidebar}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? '›' : '‹'}
          </button>

          <div className="sidebar-header">
            <div className="logo-dot">⚡</div>
            <span className="logo">Taha HQ</span>
          </div>

          {NAV_SECTIONS.map(section => (
            <div className="nav-section" key={section.label}>
              <p className="nav-label">{section.label}</p>
              {section.links.map(link => link.disabled ? (
                <span className="nav-link disabled" key={link.text} title={collapsed ? link.text : undefined}>
                  <span className="nav-icon">{link.icon}</span>
                  <span className="nav-text">{link.text}</span>
                </span>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  title={collapsed ? link.text : undefined}
                  className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                >
                  <span className="nav-icon">{link.icon}</span>
                  <span className="nav-text">{link.text}</span>
                </NavLink>
              ))}
            </div>
          ))}

          <div className="sidebar-footer">
            <div className="sidebar-footer-text" style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', paddingLeft: 10 }}>
              v1.0 · Elki Solutions
            </div>
            <button
              onClick={async () => {
                await fetch('/api/logout', { method: 'POST', credentials: 'include' });
                setAuthState('denied');
              }}
              title="Sign out"
              style={{
                background: 'none', border: 'none',
                color: 'rgba(255,255,255,0.2)', fontSize: 11,
                cursor: 'pointer', padding: collapsed ? '6px 0 0' : '6px 0 0 10px',
                textAlign: collapsed ? 'center' : 'left',
                width: '100%',
              }}
            >
              {collapsed ? '⏻' : 'Sign out'}
            </button>
          </div>
        </nav>

        <main className="main">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/1rm" element={<OneRMTracker />} />
            <Route path="/orders" element={<OrderDashboard />} />
            <Route path="/receipts" element={<ReceiptScanner />} />
            <Route path="/pokemon" element={<PokemonCenter />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}
